import { useEffect, useState } from "react";
import { Printer } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import ReceiptText from "@/components/ReceiptText";
import { previewCheckItems, printCheckItems } from "@/lib/orderApi";

const reason = err => {
  const detail = err?.response?.data?.detail;
  return typeof detail === "string" ? detail : err?.message || "unknown error";
};

/**
 * The table's Check Items slip on screen before it prints (owner, 2026-10-25:
 * "check KOT preview for phone and PC"). The text is the server's decoding of
 * the very bytes the bill printer would get, so it is the paper. Print sends
 * the real slip; closing prints nothing.
 */
export default function CheckItemsPreview({ orderId, tableName, token, onClose }) {
  const [state, setState] = useState({ phase: "loading" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    previewCheckItems(orderId, token)
      .then(data => { if (live) setState({ phase: "ready", text: data.text, printer: data.printer }); })
      .catch(err => { if (live) setState({ phase: "error", detail: reason(err) }); });
    return () => { live = false; };
  }, [orderId, token]);

  const print = async () => {
    setBusy(true);
    try {
      const { queued } = await printCheckItems(orderId, token);
      if (queued?.includes("offline_queue")) toast.warning(`${tableName}: the bill printer is offline — the check will print when it is back`);
      else toast.success(`${tableName}: check printing`);
      onClose();
    } catch (err) {
      toast.error(`Check not printed — ${reason(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={open => { if (!open) onClose(); }}>
      <DialogContent className="max-w-sm max-h-[90vh] flex flex-col" data-testid="check-items-preview">
        <DialogHeader>
          <DialogTitle>Check items — {tableName}</DialogTitle>
          <DialogDescription>
            {state.phase === "ready" ? `Prints at ${state.printer}.` : "The slip as the printer will print it."}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto rounded-md border bg-card p-4">
          {state.phase === "loading" && <p className="text-meta text-muted-foreground">Asking the server for the slip…</p>}
          {state.phase === "error" && <p className="text-meta text-danger" role="alert">Preview not loaded — {state.detail}</p>}
          {state.phase === "ready" && <ReceiptText text={state.text} testId="check-items-text" />}
        </div>
        <div className="flex justify-end gap-snug">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button variant="action" onClick={print} disabled={busy || state.phase !== "ready"} data-testid="check-items-print">
            <Printer className="h-4 w-4 mr-1.5" aria-hidden="true" />{busy ? "Printing…" : "Print check"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
